import { format, getUnixTime } from 'date-fns';
import { createSelector } from 'reselect';
import { type SnapshotFrom } from 'xstate';
import { buildColumns } from '#src/components/table/build-columns';
import { CATEGORY_LIMITS, FUNDING_SOURCE_LIMITS } from '#src/constants/limits';
import {
  type DatasetData,
  type ViewType,
  type SummaryItem,
  type OneDimensionData,
  type TwoDimensionData,
  type ThreeDimensionData,
  type TableData,
  type TableType,
  type Limits,
  type YearsMode,
  type DataPoint,
  DefaultFilterValues,
} from '#src/types';
import { getLevelLimitsByViewType } from '#src/utils/get-limit';
import { COMPARE_BY_KEYS } from './constants';
import { type machine } from './definition';
import { checkComparison } from './guards';
import * as inputSelectors from './input-selectors';
import { type MachineContextType } from './types';

// Simple selectors (no memoization needed)
export function getChartName(
  snapshot: SnapshotFrom<typeof machine>
): string | null {
  const value = inputSelectors.getStateValue(snapshot);
  let name = null;

  if (typeof value === 'object' && (value as any)?.chart != null) {
    name = (value as any).chart;
  }

  if (name != null && typeof name === 'object') {
    name = Object.keys(name)[0];
  }

  return name;
}

export function getView(snapshot: SnapshotFrom<typeof machine>): ViewType {
  return inputSelectors.getViewType(snapshot);
}

export const getUniqueMonths = createSelector(
  [inputSelectors.getIsMonthly, inputSelectors.getDatasetDataRaw],
  (isMonthly, datasetData): Record<number, number[]> => {
    if (!isMonthly) return [];

    const monthsByYear = datasetData?.reduce<Record<string, Set<number>>>(
      (acc, d) => {
        const year = d.date.getUTCFullYear();
        if (!acc[year]) {
          acc[year] = new Set();
        }
        acc[year].add(d.date.getUTCMonth());
        return acc;
      },
      {}
    );

    if (!monthsByYear) return {};

    return Object.keys(monthsByYear).reduce(
      (acc, year) => {
        acc[year] = Array.from(monthsByYear[year]).sort((a, b) => a - b);
        return acc;
      },
      {} as Record<string, number[]>
    );
  }
);

export const getShouldShowMonths = createSelector(
  [
    inputSelectors.getViewType,
    inputSelectors.getChartType,
    inputSelectors.getIsMonthly,
    getUniqueMonths,
  ],
  (viewType, chartType, isMonthly, uniqueMonths): boolean => {
    if (!isMonthly) return false;
    const monthsLength = Object.keys(uniqueMonths).length;
    if (monthsLength === 0) return false;
    if (viewType === 'table') return false;
    return viewType === 'chart' && chartType === 'area' && isMonthly;
  }
);

export const getDatasetData = createSelector(
  [inputSelectors.getDatasetDataRaw, getShouldShowMonths, getUniqueMonths],
  (datasetData, shouldShowMonths, uniqueMonths): DatasetData | null => {
    const monthsLength = Object.keys(uniqueMonths).length;
    if (datasetData != null && !shouldShowMonths && monthsLength > 0) {
      return datasetData.filter((d) => {
        const year = d.date.getUTCFullYear();
        const yearMonthsLength = uniqueMonths[year].length;

        const lastMonth = uniqueMonths[year][yearMonthsLength - 1];

        return d.date.getUTCMonth() === lastMonth;
      });
    }

    return datasetData || null;
  }
);

export const getMapChartData = createSelector(
  [
    inputSelectors.getMapChartDataRaw,
    inputSelectors.getIsMonthly,
    getUniqueMonths,
  ],
  (mapChartData, isMonthly, uniqueMonths): DatasetData | null => {
    const monthsLength = Object.keys(uniqueMonths).length;
    if (mapChartData != null && isMonthly && monthsLength > 0) {
      return mapChartData.filter((d) => {
        const year = d.date.getUTCFullYear();
        const yearMonthsLength = uniqueMonths[year].length;
        const lastMonth = uniqueMonths[year][yearMonthsLength - 1];

        return d.date.getUTCMonth() === lastMonth;
      });
    }

    return mapChartData || null;
  }
);

export const getTableType = createSelector(
  [
    inputSelectors.getTableDimensions,
    inputSelectors.getViewType,
    inputSelectors.getCompareBy,
    inputSelectors.getYearsMode,
    inputSelectors.getIsMonthly,
  ],
  (
    tableDimensions,
    viewType,
    compareBy,
    yearsMode,
    isMonthly
  ): TableType | null => {
    if (tableDimensions != null) return tableDimensions;
    // For chart/map views, infer from context
    if (viewType === 'chart' || viewType === 'map') {
      // Check if time/date is used as a dimension
      const timeIsDimension = yearsMode !== 'single' || isMonthly;

      if (timeIsDimension) {
        const totalDimensions = compareBy.length + 1;
        if (totalDimensions === 1) return 'oneDimension';
        if (totalDimensions === 2) return 'twoDimensions';
        return 'threeDimensions';
      } else {
        const totalDimensions = compareBy.length;
        if (totalDimensions <= 1) return 'oneDimension';
        if (totalDimensions === 2) return 'twoDimensions';
        return 'threeDimensions';
      }
    }

    return null;
  }
);

export const getDatasetTableData = createSelector(
  [
    getDatasetData,
    getShouldShowMonths,
    getTableType,
    inputSelectors.getCompareBy,
    inputSelectors.getLevelCompare,
    inputSelectors.getCategoryCompare,
    inputSelectors.getYearsMode,
    inputSelectors.getIsFetching,
  ],
  (
    datasetData,
    shouldShowMonths,
    tableType,
    compareBy,
    levelCompare,
    categoryCompare,
    yearsMode,
    isFetching
  ): Array<OneDimensionData | TwoDimensionData | ThreeDimensionData> => {
    if (!datasetData || isFetching || tableType == null) {
      return [] as OneDimensionData[];
    }

    const DATE_FORMAT = shouldShowMonths ? 'yyyy-MM-01' : 'yyyy-01-01';
    const context = {
      compareBy,
      levelCompare,
      categoryCompare,
      yearsMode,
    } as MachineContextType;

    switch (tableType) {
      case 'oneDimension': {
        const data: TableData<OneDimensionData> = {};
        datasetData?.forEach((item) => {
          const yearKey = format(item.date, DATE_FORMAT);
          const timestamp = getUnixTime(new Date(yearKey));
          let dimensionKey: string | number = timestamp;
          let levelKey =
            compareBy.includes('level') || levelCompare.length === 1
              ? 'sublevelId'
              : ('level' as keyof typeof item);
          let categoryKey =
            compareBy.includes('category') || categoryCompare.length === 1
              ? 'subcategoryId'
              : ('category' as keyof typeof item);

          if (
            yearsMode === 'single' &&
            !shouldShowMonths &&
            checkComparison(context, 'some')
          ) {
            const itemPropKeys = {
              level: levelKey,
              category: categoryKey,
              fundingSource: COMPARE_BY_KEYS.fundingSource,
            };
            const itemProp = itemPropKeys[compareBy[0]];
            dimensionKey = item[itemProp] as string;
          }

          data[dimensionKey] = {
            value: item.value,
            date: timestamp,
            level: item[levelKey] as string,
            category: item[categoryKey] as string,
            fundingSource: item[COMPARE_BY_KEYS.fundingSource] as string,
            percentGrowthLastYear: item.percentGrowthLastYear,
          };
        });
        return Object.values(data);
      }
      case 'twoDimensions': {
        const data: TableData<TwoDimensionData> = {};
        datasetData?.forEach((item) => {
          const yearKey = format(item.date, DATE_FORMAT);
          const timestamp = getUnixTime(new Date(yearKey));
          let dimension1Key: string | number = timestamp;

          const levelKey =
            compareBy.includes('level') || levelCompare.length === 1
              ? 'sublevelId'
              : 'level';
          const categoryKey =
            compareBy.includes('category') || categoryCompare.length === 1
              ? 'subcategoryId'
              : 'category';

          const itemPropKeys = {
            level: levelKey,
            category: categoryKey,
            fundingSource: COMPARE_BY_KEYS.fundingSource,
          };

          let dimension2Key = item[
            itemPropKeys[compareBy[0]] as keyof typeof item
          ] as string;

          if (yearsMode === 'single' && !shouldShowMonths) {
            // when we are in single year mode, we need to use the compareBy as the 2 dimensions
            // this means that index 0 becomes the first dimension and index 1 the second
            dimension1Key = item[
              itemPropKeys[compareBy[0]] as keyof typeof item
            ] as string;

            dimension2Key = item[
              itemPropKeys[compareBy[1]] as keyof typeof item
            ] as string;
          }

          if (!data[dimension1Key]) {
            data[dimension1Key] = {};
          }

          data[dimension1Key][dimension2Key as any] = {
            value: item.value,
            date: timestamp,
            level: item[levelKey as keyof typeof item] as string,
            category: item[categoryKey as keyof typeof item] as string,
            fundingSource: item[COMPARE_BY_KEYS.fundingSource] as string,
            percentGrowthLastYear: item.percentGrowthLastYear,
          } as TwoDimensionData[string];
        });
        return Object.values(data).flatMap((d1) => Object.values(d1));
      }
      case 'threeDimensions': {
        const data: TableData<ThreeDimensionData> = {};
        datasetData?.forEach((item) => {
          const yearKey = format(item.date, DATE_FORMAT);
          const timestamp = getUnixTime(new Date(yearKey));

          if (!data[timestamp]) {
            data[timestamp] = {};
          }
          const levelKey =
            compareBy.includes('level') || levelCompare.length === 1
              ? 'sublevelId'
              : 'level';
          const categoryKey =
            compareBy.includes('category') || categoryCompare.length === 1
              ? 'subcategoryId'
              : 'category';

          const itemPropKeys = {
            level: levelKey,
            category: categoryKey,
            fundingSource: COMPARE_BY_KEYS.fundingSource,
          };

          const dimension1Key = item[
            itemPropKeys[compareBy[0]] as keyof typeof item
          ] as string;
          const dimension2Key = item[
            itemPropKeys[compareBy[1]] as keyof typeof item
          ] as string;

          const threeDimensionData = data as TableData<ThreeDimensionData>;
          let existing: ThreeDimensionData[string] | undefined =
            threeDimensionData[timestamp][dimension1Key];
          if (!existing) {
            existing = threeDimensionData[timestamp][dimension1Key] =
              {} as ThreeDimensionData[string];
          }

          threeDimensionData[timestamp][dimension1Key] = {
            ...existing,
            [dimension2Key as string]: {
              value: item.value,
              date: timestamp,
              level: item[levelKey as keyof typeof item] as string,
              category: item[categoryKey as keyof typeof item] as string,
              fundingSource: item[COMPARE_BY_KEYS.fundingSource] as string,
              percentGrowthLastYear: item.percentGrowthLastYear,
            },
          } as ThreeDimensionData[string];
        });
        return Object.values(data).flatMap((d1) => Object.values(d1));
      }
    }
  }
);

export const getDatasetsSummaryData = createSelector(
  [
    inputSelectors.getSummary,
    inputSelectors.getDatasetOptions,
    inputSelectors.getSelectedYears,
  ],
  (
    summary,
    datasetOptions,
    selectedYears
  ):
    | (SummaryItem & {
        name: string;
      })[]
    | null => {
    const currentSummary =
      summary?.filter((s) => s.year === selectedYears[0]) ?? null;

    const names = datasetOptions.reduce(
      (acc, dataset) => {
        acc[dataset.id] = dataset.name;
        return acc;
      },
      {} as Record<string, string>
    );

    return (
      currentSummary &&
      currentSummary
        // filter out datasets that are not in the datasetOptions
        .filter((dataset) => names[dataset.id] != null)
        .map((dataset) => ({
          ...dataset,
          name: names[dataset.id],
        }))
    );
  }
);

export function getContextualData(
  snapshot: SnapshotFrom<typeof machine>
): DatasetData | null {
  return inputSelectors.getContextualDataRaw(snapshot) || null;
}

export const getDatasetDataLabels = createSelector(
  [
    inputSelectors.getLevel,
    inputSelectors.getCategory,
    inputSelectors.getFundingSource,
    inputSelectors.getLevelOptions,
    inputSelectors.getCategoryOptions,
    inputSelectors.getFundingSources,
    inputSelectors.getDatasetOptions,
    inputSelectors.getDataset,
    inputSelectors.getContextualDataset,
  ],
  (
    level,
    category,
    fundingSource,
    levelOptions,
    categoryOptions,
    fundingSources,
    datasetOptions,
    dataset,
    contextualDataset
  ) => {
    if (!levelOptions || !categoryOptions) {
      return null;
    }

    const datasetObj = datasetOptions.find((opt) => opt.id === dataset);
    const contextualDatasetObj = datasetOptions.find(
      (opt) => opt.id === contextualDataset
    );

    const datasetUnit = datasetObj?.unitName as string;
    const contextualDatasetUnit = contextualDatasetObj?.unitName as string;

    // we set the dataset name as the value for the level and category labels
    // to display when there's only one dimension and the label is already displayed as the axis label
    const datasetName = datasetObj?.name as string;
    const contextualDatasetName = (contextualDatasetObj?.shortName ??
      contextualDatasetObj?.name) as string;
    const labels: {
      datasetName: string;
      datasetUnit: string;
      contextualDatasetUnit: string;
      contextualDatasetName: string;
      level: Record<string, string>;
      category: Record<string, string>;
      fundingSource: Record<string, string>;
    } = {
      datasetName,
      datasetUnit,
      contextualDatasetUnit,
      contextualDatasetName,
      level: {
        value: datasetName,
        [level]: levelOptions[level].name,
      },
      category: {
        value: datasetName,
        [category]: categoryOptions[category].name,
      },
      fundingSource: {
        value: datasetName,
        [fundingSource]: fundingSources?.[fundingSource]?.name ?? '',
      },
    };

    ['level', 'category', 'fundingSource'].forEach((by) => {
      const key = by as 'level' | 'category' | 'fundingSource';
      const valuesByCompareBy = {
        level: level,
        category: category,
        fundingSource: fundingSource,
      };
      const value = valuesByCompareBy[key];
      let data = null;
      if (key === 'level') {
        data = levelOptions?.[value];
      } else if (key === 'category') {
        data = categoryOptions?.[value];
      } else if (key === 'fundingSource') {
        data = fundingSources?.[fundingSource];
      }
      if (!data) {
        console.error(`Missing data for ${key} ${value}`);
        return;
      }

      if (
        [
          DefaultFilterValues.level,
          DefaultFilterValues.category,
          DefaultFilterValues.fundingSource,
        ].includes(value as DefaultFilterValues)
      ) {
        labels[key][value] = data.shortName ?? data.name;
      }

      if (data.options) {
        data.options.forEach((opt) => {
          labels[key][opt.id] = opt.name;
        });
      }
    });

    return labels;
  }
);

export const getDownloadData = createSelector(
  [
    getDatasetTableData,
    getTableType,
    getDatasetDataLabels,
    getShouldShowMonths,
    // breaks all optimizations, but it's only used for the download data
    inputSelectors.getContext,
  ],
  (
    tableData,
    tableType,
    datasetLabels,
    shouldShowMonths,
    context
  ): Record<string, unknown>[] | null => {
    if (!tableData || tableData.length === 0 || !tableType) {
      return null;
    }

    // Calculate totals before building columns to avoid recalculation
    let totals:
      | { total: number }
      | { columnTotals: Record<string, number>; columns: string[] }
      | null = null;

    switch (tableType) {
      case 'oneDimension':
      case 'twoDimensions': {
        totals = {
          total: (tableData as OneDimensionData[]).reduce(
            (acc, row) => acc + row.value,
            0
          ),
        };
        break;
      }
      case 'threeDimensions': {
        // Collect columns and calculate totals in a single pass
        const columnTotals: Record<string, number> = {};
        (tableData as TwoDimensionData[]).forEach((row) => {
          Object.keys(row).forEach((key) => {
            if (!columnTotals[key]) {
              columnTotals[key] = 0;
            }
            columnTotals[key] += row[key]?.value ?? 0;
          });
        });

        const columns = Object.keys(columnTotals).sort();
        totals = { columnTotals, columns };
        break;
      }
    }

    const columns = buildColumns(
      tableData,
      shouldShowMonths,
      context,
      datasetLabels,
      tableType,
      totals
    );

    if (!columns) {
      return null;
    }

    // Pre-build the result array for better performance
    const result = new Array(tableData.length);
    for (let i = 0; i < tableData.length; i++) {
      const item = tableData[i];
      const row: Record<string, any> = {};
      for (let j = 0; j < columns.length; j++) {
        const column = columns[j];
        row[column.header as string] = column.accessorFn(item, i);
      }
      result[i] = row;
    }
    return result;
  }
);

export const getFiltersLimits = createSelector(
  [
    inputSelectors.getCompareBy,
    inputSelectors.getCategoryOptions,
    inputSelectors.getLevelOptions,
    inputSelectors.getFundingSources,
    inputSelectors.getViewType,
  ],
  (
    compareBy,
    categoryOptions,
    levelOptions,
    fundingSources,
    viewType
  ): {
    level: Record<string, Limits>;
    category: Record<string, Limits>;
    fundingSource: Record<string, Limits>;
  } => {
    const defaultCategoryLimits = Object.keys(categoryOptions ?? {}).reduce(
      (acc, category) => {
        acc[category] = { ...CATEGORY_LIMITS[viewType] };
        return acc;
      },
      {} as Record<string, Limits>
    );

    const defaultFundingSourceLimits = Object.keys(fundingSources ?? {}).reduce(
      (acc, fundingSource) => {
        acc[fundingSource] = { ...FUNDING_SOURCE_LIMITS[viewType] };
        return acc;
      },
      {} as Record<string, Limits>
    );

    const limits = {
      level: getLevelLimitsByViewType(viewType),
      category: defaultCategoryLimits,
      fundingSource: defaultFundingSourceLimits,
    };

    const compareByOptions = {
      level: levelOptions,
      category: categoryOptions,
      fundingSource: fundingSources,
    };

    ['level', 'category', 'fundingSource'].forEach((by) => {
      if (
        compareBy.length === 2 &&
        !compareBy.includes(by as 'level' | 'category' | 'fundingSource')
      ) {
        limits[by as 'level' | 'category' | 'fundingSource'] = Object.keys(
          compareByOptions[by as 'level' | 'category' | 'fundingSource'] ?? {}
        ).reduce(
          (acc, option) => {
            const optionLimits =
              limits[by as 'level' | 'category' | 'fundingSource'][option] ??
              limits[by as 'level' | 'category' | 'fundingSource'].default;
            acc[option] = {
              ...optionLimits,
              comparisonLimit: 1,
            };
            return acc;
          },
          {} as Record<string, Limits>
        );
      }
    });

    return limits;
  }
);

export function getMultipleYearsLimit({
  context,
}: {
  context: MachineContextType;
}): number {
  const {
    data: { isMonthly, yearsOptions },
  } = context;

  if (isMonthly && checkComparison(context)) {
    return 3;
  }

  return yearsOptions.length;
}

export function getYearModes(
  snapshot: SnapshotFrom<typeof machine>
): YearsMode[] {
  const viewType = inputSelectors.getViewType(snapshot);

  if (viewType === 'map') {
    return ['single'];
  }

  return ['single', 'multiple', 'range'];
}

export const getDatasetDataForMap = createSelector(
  [getDatasetData, inputSelectors.getLevelOptions, inputSelectors.getLevel],
  (
    data,
    levelOptions,
    level
  ): Record<string, DataPoint & { name: string | null }> | null => {
    if (!data) return null;
    const options = levelOptions?.[level]?.options;
    const optionsById =
      options?.reduce(
        (acc, opt) => {
          acc[opt.id] = opt;
          return acc;
        },
        {} as Record<string, (typeof options)[number]>
      ) ?? null;
    return data.reduce(
      (acc, item) => {
        if (item.geoId != null) {
          let name = levelOptions?.[level]?.shortName as string;
          if (item.sublevelId != null && optionsById != null) {
            name = optionsById[item.sublevelId]?.name as string;
          }

          acc[item.geoId] = {
            ...item,
            name,
          };
        }
        return acc;
      },
      {} as Record<string, DataPoint & { name: string | null }>
    );
  }
);

export const getGeoData = createSelector(
  [
    inputSelectors.getGeoData,
    inputSelectors.getLevelOptions,
    inputSelectors.getCategoryOptions,
    inputSelectors.getLevel,
    inputSelectors.getCategory,
    inputSelectors.getSelectedYears,
  ],
  (
    geoDataArray,
    levelOptions,
    categoryOptions,
    level,
    category,
    selectedYears
  ): Record<string, string> | null => {
    if (!geoDataArray || !selectedYears || selectedYears.length === 0) {
      return null;
    }

    // Filter geoData by the selected year
    const currentYear = selectedYears[0];
    const geoDataItem = geoDataArray.find((item) => item.year === currentYear);

    if (!geoDataItem) {
      return null;
    }

    const geoData = geoDataItem.geoData;

    if (categoryOptions) {
      const categoryGeoLevelId =
        categoryOptions?.[category as string]?.geoLevelId;

      if (categoryGeoLevelId != null) {
        return geoData?.[categoryGeoLevelId] ?? null;
      }
    }

    if (levelOptions) {
      const levelGeoLevelId = levelOptions?.[level as string]?.geoLevelId;
      if (levelGeoLevelId != null) {
        return geoData?.[levelGeoLevelId] ?? null;
      }
    }

    return null;
  }
);

export const getShouldSplitAxes = createSelector(
  [
    inputSelectors.getDatasetOptions,
    inputSelectors.getDataset,
    inputSelectors.getContextualDataset,
  ],
  (datasetOptions, dataset, contextualDataset) => {
    if (!datasetOptions || !dataset || !contextualDataset) {
      return false;
    }
    const datasetUnit = datasetOptions.find((opt) => opt.id === dataset)?.unit;
    const contextualDatasetUnit = datasetOptions.find(
      (opt) => opt.id === contextualDataset
    )?.unit;
    return datasetUnit !== contextualDatasetUnit;
  }
);
